import utils from 'ellacard-video-utils';


function inputIsValid(section, input) {
  if (!input)
    return !section.spec.required;
  const validate = utils.GRAPH_INPUTS[section.type].validate
  return !validate || validate(input, section);
}


function getSectionTitle(section) {
  return section.title || 'Input';
}


function buildTemplateModule(code, assets) {
  // Returns the module 'code' as a callable function
  // Note: this is inherently unsafe. What if a nefarious designer submitted a template for our approval that had
  // malicious code in it? We would run it with our admin privileges, and someone who knew what they were doing
  // could execute any API with admin privileges, or more likely simply steal the JWT from local storage. One
  // potential workaround is to have a sandboxed user with hamstrung permissions specifically for revieweing
  // template submissions. Definitely doesn't seem important right now though...
  if (!code)
    code = `const { AbstractProjectBuilder } = require('utils'); class Builder extends AbstractProjectBuilder {} module.exports = Builder;`;
  const fn = new Function('require', 'module', code);
  const pseudoRequire = moduleName => {
    if (moduleName == 'utils')
      return utils;
    if (moduleName == 'assets') {
      // We only include the 'assets' as a pseudo-module for legacy templates. It is also passed into the Builder's
      // constructor.
      return assetId => {
        if (!assets[assetId])
          throw new Error(`Asset '${assetId}' not found`);
        return assets[assetId].src;
      };
    }
    throw new Error(`Module '${moduleName}' not found`);
  };
  const pseudoModule = { exports: {} };
  fn(pseudoRequire, pseudoModule);
  if (typeof pseudoModule.exports != 'function')
    throw new Error('module.exports is not a class / function');
  return pseudoModule.exports;
}


function getCrop(srcAspectRatio, r, fixedAspectRatio) {
  // Builds the default crop structure
  if (!fixedAspectRatio)
    return null;

  const crop = { left: 0, top: 0, right: 0, bottom: 0 };
  let horizontalKeys = [ 'left', 'right' ];
  let verticalKeys = [ 'top', 'bottom' ];
  if (r % 180 != 0) {
    srcAspectRatio = 1 / srcAspectRatio;
    [ horizontalKeys, verticalKeys ] = [ verticalKeys, horizontalKeys ];
  }
  if (fixedAspectRatio > srcAspectRatio)
    horizontalKeys.map(key => crop[key] = (1 - fixedAspectRatio / srcAspectRatio) / 2);
  else
    verticalKeys.map(key => crop[key] = (1 - srcAspectRatio / fixedAspectRatio) / 2);
  return crop;
}


function parseColor(color) {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 1;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, 1, 1);
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, 1, 1);
  return ctx.getImageData(0, 0, 1, 1).data;
}


function getBrightness(color) {
  const [ r, g, b ] = parseColor(color);
  // This algorithm gives you the preceived brightness of a color in the range [0,255]
  // https://www.w3.org/TR/AERT/#color-contrast
  return (299 * r + 587 * g + 114 * b) / 1000;
}


function getLuminanace(color) {
  const [ r, g, b ] = parseColor(color).map(v => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return Number((0.2126 * r + 0.7152 * g + 0.0722 * b).toFixed(3));
};


function getContrastRatio(a, b) {
  const lumA = getLuminanace(a);
  const lumB = getLuminanace(b);
  return (Math.max(lumA, lumB) + 0.05) / (Math.min(lumA, lumB) + 0.05);
};


function getColorDistance(a, b) {
  const [ r1, g1, b1 ] = parseColor(a);
  const [ r2, g2, b2 ] = parseColor(b);
  return Math.sqrt((r2 - r1) ** 2 + (g2 - g1) ** 2 + (b2 - b1) ** 2);
}


function getBackground(color) {
  return getBrightness(color) < 92 ? '#FFF' : '#000';
}


function getAverageColor(background) {
  if (!background.img)
    return background.color;
  return new Promise(
    async (resolve, reject) => {
      const img = new Image();

      // Use an object URL to skirt any cross-origin issues
      img.src = URL.createObjectURL(await fetch(background.img).then(r => r.blob()));;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.fillStyle = background.color || '#FFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        const data = ctx.getImageData(0, 0, img.width, img.height).data;
        const rgb = [ 0, 0, 0 ];
        for (let i = 0; i < data.length; i += 4)
          for (let j = 0; j < 3; j++)
            rgb[j] += data[i + j];
        canvas.remove();
        resolve(`rgb(${rgb.map(x => x / (data.length / 4)).join(',')})`);
      };
      img.onerror = reject;
    }
  );
}


function getRandomContrastingColor(src, targets) {
  targets = targets.filter(x => (getContrastRatio(src, x) >= 3.5) && (getColorDistance(src, x) >= 100));
  return targets[ Math.floor(Math.random() * targets.length) ];
}


export { buildTemplateModule, inputIsValid, getSectionTitle, getCrop, getBrightness, getLuminanace, getContrastRatio, getColorDistance, getBackground, getAverageColor, getRandomContrastingColor };
